'use client';
import React from 'react';
import keyBy from 'lodash/keyBy';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import Box from '@mui/material/Box';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { styled } from '@mui/material';
import OpenInFull from '@mui/icons-material/OpenInFull';
import Close from '@mui/icons-material/Close';
import ContentModule from '../ContentModule';
import LRText from './LrText';
import { detectLanguage } from '../../utils/detect-code/languageDetector'; // Debug with detectLanguageScores
import { TextProps } from './Text.types';

/**
 * Purpose of this local <Text /> is:
 * - Add ids and data-scrollspy attributes to H2 for article page links
 * - Render <br> tags from Markdown
 * - Parse MUI Table elements
 */

const getTextMarkClass = (marks: any) => {
  return (marks || [])
    .filter((mark: any) => mark.type !== 'code')
    .map((mark: any) => `${mark.type}-text`)
    .join(' ');
};

const renderCodeText = (node: any) => {
  const lines = node?.value?.split('\n');
  return (lines || [])
    .filter((text: any) => text)
    .map((text: any, index: number) => (
      <>
        <Box component="code" className={getTextMarkClass(node.marks)}>
          {text}
        </Box>
        {index < lines.length - 1 ? <br /> : null}
      </>
    ));
};

const renderCodeString = (content: any) => {
  const codeString = content
    .map((text: any) => text.value)
    .filter((text: any) => text)
    .join('\n');

  return codeString;
};

const codeEditorOptions = {
  lineNumbers: false,
  defaultColorDecorators: false,
  hover: false
};

const renderCodeBlock = (content: any) => {
  const codeString = renderCodeString(content);
  const language = detectLanguage(codeString);

  return (
    <ContentModule
      __typename="CodeSnippet"
      defaultCode={codeString}
      defaultCodeType={language}
      editorOptions={codeEditorOptions}
      editorTheme="vs-dark"
    />
  );
};

const renderNodeOptions = ({ links }: { links?: any }) => {
  const entries = keyBy(links?.entries ?? [], 'id');
  const assets = keyBy(links?.assets ?? [], 'id');

  return {
    [BLOCKS.HEADING_1]: (node: any, children: any) => (
      <Typography id={node.data.id} variant="h1" data-scrollspy>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_2]: (node: any, children: any) => (
      <Typography id={node.data.id} variant="h2" data-scrollspy>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_3]: (node: any, children: any) => (
      <Typography id={node.data.id} variant="h3" data-scrollspy>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_4]: (node: any, children: any) => (
      <Typography id={node.data.id} variant="h4" data-scrollspy>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_5]: (node: any, children: any) => (
      <Typography id={node.data.id} variant="h5" data-scrollspy>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_6]: (node: any, children: any) => (
      <Typography id={node.data.id} variant="h6" data-scrollspy>
        {children}
      </Typography>
    ),
    'html': (node: any, _children: any) => {
      return node.data.value;
    },
    'markdown-image': (node: any) => {
      const url: string | undefined = node?.data?.url;
      if (!url) return null;
      return (
        <Box
          component="img"
          src={url}
          alt={node?.data?.alt ?? ''}
          loading="lazy"
          sx={{ display: 'inline-block', maxWidth: '100%', height: 'auto' }}
        />
      );
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      if (
        node.content?.length &&
        node.content.every((text: any) => text.marks?.find((mark: any) => mark.type === 'code'))
      ) {
        return renderCodeBlock(node.content);
      }
      return children?.length === 1 && children[0] === '' ? (
        <br />
      ) : (
        <Typography variant="body1" data-testid={`Text-body1`}>
          {node.content.map((text: any, index: number) =>
            text?.marks?.length && text.marks.find((mark: any) => mark.type === 'code')
              ? renderCodeText(text)
              : children[index]
          )}
        </Typography>
      );
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
      const id: string = node?.data?.target?.sys?.id;
      const entry = entries[id];
      return <ContentModule {...entry} data-testid={`Texts-${BLOCKS.EMBEDDED_ENTRY}`} />;
    },
    [INLINES.EMBEDDED_ENTRY]: (node: any) => {
      const id: string = node?.data?.target?.sys?.id;
      const entry = entries[id];
      return <ContentModule {...entry} data-testid={`Texts-${INLINES.EMBEDDED_ENTRY}`} />;
    },
    [INLINES.ASSET_HYPERLINK]: (node: any) => {
      const id: string = node?.data?.target?.sys?.id;
      const asset = assets[id];
      const content = node?.content[0]?.value;

      return (
        <ContentModule {...asset} data-testid={`Texts-${INLINES.ASSET_HYPERLINK}`}>
          {content}
        </ContentModule>
      );
    },
    [INLINES.ENTRY_HYPERLINK]: (node: any, children: any) => {
      const id: string = node?.data?.target?.sys?.id;
      const entry = entries[id];
      const content = node?.content[0]?.value;
      if (!content && !entry) {
        return null;
      }

      // Handle Media content items like direct asset references
      if (entry?.__typename === 'Media') {
        return (
          <ContentModule
            __typename="Link"
            href={entry?.file?.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="Text-media-entry-hyperlink">
            {content || children}
          </ContentModule>
        );
      }

      return (
        <ContentModule {...entry} text={content || entry.text} data-testid={`Texts-${INLINES.ENTRY_HYPERLINK}`}>
          {content}
        </ContentModule>
      );
    },
    [BLOCKS.TABLE]: (node: any, children: any) => {
      let header;
      if (node.content[0].nodeType === 'table-header') {
        header = children[0];
        children = children.slice(1);
      }
      return (
        <>
          <TableDialog header={header}>{children}</TableDialog>
          <TableContainer sx={{ '[class$=Media-root]': { width: 'auto' }, '& td': { whiteSpace: 'break-spaces' } }}>
            <Table>
              {header ? header : null}
              <TableBody>{children}</TableBody>
            </Table>
          </TableContainer>
        </>
      );
    },
    'table-header': (_: any, children: any) => {
      return (
        <TableHead>
          <TableRow>{children}</TableRow>
        </TableHead>
      );
    },
    [BLOCKS.TABLE_HEADER_CELL]: (_: any, children: any) => {
      return <TableCell>{children}</TableCell>;
    },
    [BLOCKS.TABLE_ROW]: (_: any, children: any) => {
      return <TableRow>{children}</TableRow>;
    },
    [BLOCKS.TABLE_CELL]: (_: any, children: any) => {
      return <TableCell>{children}</TableCell>;
    },
    [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
      const id: string = node?.data?.target?.sys?.id;
      const entry = assets[id];

      return (
        <Box textAlign="center">
          <ContentModule
            {...entry}
            sx={{
              maxWidth: entry?.file?.width ? `${entry.file.width}px` : 'none'
            }}
            data-testid={`Text-${BLOCKS.EMBEDDED_ASSET}`}
          />
        </Box>
      );
    }
  };
};

const Text = (props: TextProps) => {
  const renderNode = React.useMemo(() => renderNodeOptions({ links: props?.body?.links }), [props]);
  return (
    <Root>
      <LRText {...props} renderNode={renderNode} />
    </Root>
  );
};

const TableDialog = ({ header, children }: { header: any; children: any }) => {
  const [open, setOpen] = React.useState(false);

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Box
        displayPrint="none"
        width="100%"
        alignItems="flex-end"
        justifyContent="flex-end"
        sx={{ display: { xs: 'none', md: 'flex' } }}>
        <Button
          size="small"
          sx={{ 'fontSize': 14, '& .MuiButton-startIcon >*:nth-of-type(1)': { fontSize: 16 } }}
          onClick={handleClick}
          startIcon={<OpenInFull />}>
          Expand table
        </Button>
      </Box>
      <Dialog open={open} PaperProps={{ sx: { maxWidth: '100%', position: 'relative' } }} onClose={handleClose}>
        <DialogContent>
          <TableContainer
            sx={{
              '& td': {
                whiteSpace: 'break-spaces'
              }
            }}>
            <Table>
              {header ? header : null}
              <TableBody>{children}</TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <Box position="absolute" top={8} right={8}>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </Dialog>
    </>
  );
};

const Root = styled(Box, {
  name: 'Text',
  slot: 'root'
})<{}>(() => ({
  '& ol': {
    'listStyleType': 'decimal',
    '& ol': {
      'listStyleType': 'upper-alpha',
      '& ol': {
        'listStyleType': 'lower-roman',
        '& img': {
          verticalAlign: 'top'
        }
      }
    }
  }
}));

export default Text;
